import React from 'react';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import NoWiFiSymbol from './components/errors/no_wifi';
import FullViewportHeight from './components/common/full_viewport_height';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this._reload = this.reload.bind(this);
  }

  componentDidCatch(error, errorInfo) {
    // ChunkLoadError instances happen when someone is using an older version of the site, with a stale index.html file
    // that references old non-existent JS/CSS assets. Refreshing immediately fixes the problem, since the user will be
    // served the latest index.html by design.
    // See: https://ianwalter.dev/fixing-chunkloaderror/
    if (error.name === 'ChunkLoadError') {
      this._reload();
    } else {
      this.setState({ error: error });
      // Report the error to Sentry.
      Sentry.withScope(scope => {
        Object.keys(errorInfo).forEach(key => {
          scope.setExtra(key, errorInfo[key]);
        });
        Sentry.captureException(error);
      });
    }
  }

  reload() {
    window.location.reload();
  }

  render() {
    // In case we messed up, we lie to users and tell them they have no internet. Better to make the user uncertain
    // about his own connection than uncertain about the state of our web app.
    if (this.state.error) {
      return (
        <GlobalErrorBoundaryFallback>
          <NoWiFiSymbol />
          <GlobalErrorBoundaryFallbackText>
            Check your internet connection!
          </GlobalErrorBoundaryFallbackText>
          <GlobalErrorBoundaryFallbackLink type='button' onClick={this._reload}>
            Try Again
          </GlobalErrorBoundaryFallbackLink>
        </GlobalErrorBoundaryFallback>
      );
    }
    return this.props.children;
  }
}

const GlobalErrorBoundaryFallback = styled(FullViewportHeight)`
  width: 100vw;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const GlobalErrorBoundaryFallbackText = styled.p`
  font-size: 1.4em;
  text-align: center;
  margin-top: -32px;
  margin-bottom: 16px;
`;

const GlobalErrorBoundaryFallbackLink = styled.button`
  &, &:hover {
    font-size: 1.2em;
    border: none;
    background: none;
    text-decoration: underline;
    cursor: pointer;
    color: #0070c9;
  }
`;

export default AppErrorBoundary;
